import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Icon,
  Badge,
  IconButton,
  Select,
  Button,
  useDisclosure,
  Switch,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import {
  MdCheckCircle,
  MdEdit,
  MdOutlineError,
  MdPending,
  MdVisibility,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import Pagination from 'components/pagination';
import { useNavigate } from 'react-router-dom';
import RoleModal from './RoleModal';
import ErrorMessage from 'components/error-message';
import Loader from 'loaders/Loader';
import moment from 'moment';
import { createAPIEndPoint } from 'config/api/api';

// Column Helper
const columnHelper = createColumnHelper();

// TeamTable Component
export default function RoleTable(props) {
  const { tableData, loading, fetchAgain } = props;
  const navigate = useNavigate();
  const [type, setType] = React.useState('add');
  const [selectedLocation, setSelectedLocation] = React.useState(null);
  const [sorting, setSorting] = React.useState([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');




  // Function to handle the API call
  const handleStatusChange = async (rowIndex, endpoint) => {
    try {
      // Assuming you're sending the request to the API with rowIndex (or any other necessary data)
      const response = await createAPIEndPoint(`clinic_roles/${rowIndex}`).patch()
      console.log('Status updated:', response.data);
      fetchAgain()
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };


  // Columns definition
  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: '#',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),

    columnHelper.accessor('role_name', {
      id: 'role',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          ROLE NAME
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue().toLowerCase();
        const roleColors = {
          superadmin: 'purple',
          admin: 'red',
          lab: 'blue',
          staff: 'green',
          manager: 'orange',
          billing: 'pink',
          default: "gray.500",
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="60px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Status
        </Text>
      ),
      cell: (info) => {
        const role = info.getValue();
        const roleColors = {
          active: 'green',
          inactive: 'red',
        };

        return (
          <Badge
            colorScheme={roleColors[role] || 'gray'}
            px={2}
            py={1.5}
            borderRadius="md"
            textAlign="center"
            minWidth="60px"
          >
            <Text fontSize="sm" textTransform="capitalize" fontWeight="700">
              {role}
            </Text>
          </Badge>
        );
      },
    }),

    // columnHelper.accessor('created_at', {
    //   id: 'created_at',
    //   header: () => (
    //     <Text fontSize="12px" color="gray.400" fontWeight="bold">
    //       Created At
    //     </Text>
    //   ),
    //   cell: (info) => (
    //     <Text color={textColor} fontSize="sm" fontWeight="700">
    //       {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}


    //     </Text>
    //   ),
    // }),
    // columnHelper.accessor('updated_at', {
    //   id: 'updated_at',
    //   header: () => (
    //     <Text fontSize="12px" color="gray.400" fontWeight="bold">
    //       Updated At
    //     </Text>
    //   ),
    //   cell: (info) => (
    //     <Text color={textColor} fontSize="sm" fontWeight="700">
    //       {info.getValue() ? moment(info.getValue()).format('MM/DD/YYYY') : 'N/A'}

    //     </Text>
    //   ),
    // }),
    columnHelper.accessor('status', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Action

        </Text>
      ),
      cell: (info) => <><Switch
        isChecked={info.row.original.status === "active" ? true : false}
        onChange={() => handleStatusChange(info.row.original.id)}
        colorScheme="brand"
      /></>,
    }),

  ];

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [data, setData] = React.useState([]);

  const table = useReactTable({
    data: data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  React.useEffect(() => {
    if (tableData && tableData.length > 0) {
      setData([...tableData]);
    }
  }, [tableData]);

  const [searchTerm, setSearchTerm] = React.useState('');

  React.useEffect(() => {
    if (!tableData) return;

    let filteredData = [...tableData]; // Ensure you work on a copy

    filteredData = filteredData.filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.first_name
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()) ||
        item.last_name
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()) ||
        item.email
          ?.trim()
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase());

      return matchesSearch;
    });

    setData(filteredData);
  }, [tableData, searchTerm]);

  const itemsPerPageOptions = [10, 20, 30, 50];
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);

  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Text
            color={textColor}
            fontSize="24px"
            fontWeight="700"
            lineHeight="100%"
            mb={{ base: '8px', md: '0px' }}
            textAlign={{ base: 'center', md: 'left' }}
          >
            Clinic Roles

          </Text>

          <Box
            ml={{ base: 0, md: 'auto' }}
            mr={{ base: 0, md: 5 }}
            w={{ base: '100%', md: 'auto' }}
          >
            <SearchBar
              width="100%"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>

          <Box w={{ base: '100%', md: 'auto' }}>
            <Button
              w="100%"
              // minW={{ base: 'auto', md: '140px' }}
              variant="brand"
              fontWeight="500"
              onClick={() => {
                onOpen();
                setType('add');
              }}
              minH={"36px"}
              borderRadius="16px"
              mb={0}
              size="sm"
            >
              Create New Role
            </Button>
          </Box>
        </Flex>

        <Flex
          px={{ base: '12px', md: '25px' }}
          mb="8px"
          justifyContent="space-between"
          align="center"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 2, md: 0 }}
          w="100%"
        >
          <Box w={{ base: '100%', md: 'auto' }}>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width={{ base: '100%', md: 'fit-content' }}
              borderRadius="16px"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          </Box>

          <Box
            w={{ base: '100%', md: 'auto' }}
            textAlign={{ base: 'center', md: 'right' }}
          >
            <Pagination
              totalPages={totalPages}
              initialPage={currentPage}
              onPageChange={handlePageChange}
            />
          </Box>
        </Flex>

        <Box overflowX="auto">
          <Table variant="striped" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {paginatedData.map((row, index) => (
                <Tr key={index}>
                  {table
                    .getRowModel()
                    .rows.find((r) => r.original === row) // Ensure correct row mapping
                    ?.getVisibleCells()
                    ?.map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {!loading &&
          data.length === 0 &&
          (searchTerm.length > 0 ? (
            <ErrorMessage source="results" />
          ) : (
            <ErrorMessage source="roles" />
          ))}
      </Card>

      <RoleModal
        isOpen={isOpen}
        onClose={onClose}
        type={type}
        initialData={selectedLocation}
        fetchAgain={fetchAgain}
      />
    </>
  );
}
